import React from 'react';
import moment from 'moment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { getHotelDateFormat, getHotelDateTimeFormat, getHotelNowMoment } from '../../../utils/dateUtils';

function isValidMomentValue(value) {
  return value != null && moment.isMoment(value) && value.isValid();
}

function clampToMaxDateTime(value, maxDateTime) {
  if (!isValidMomentValue(value) || !maxDateTime) return value;
  return value.isAfter(maxDateTime) ? maxDateTime.clone() : value;
}

/** MUI DatePicker with hotel-configured display format (API logic stays YYYY-MM-DD). */
export function HotelDatePicker({ format, ...props }) {
  return <DatePicker format={format || getHotelDateFormat()} {...props} />;
}

/**
 * MUI DateTimePicker with hotel-configured date + time format.
 * Ignores null/invalid onChange events so keyboard entry is not reset to "now"
 * while the user is typing (consumers must not use `onChange={(v) => set(v || moment())}`).
 * Pass disableFuture to block payment/collection times after hotel "now".
 */
export function HotelDateTimePicker({
  format,
  onChange,
  onAccept,
  disableFuture = false,
  maxDateTime,
  ...props
}) {
  const resolvedMaxDateTime = maxDateTime ?? (disableFuture ? getHotelNowMoment() : undefined);

  const handleChange = (newValue, context) => {
    if (isValidMomentValue(newValue)) {
      onChange?.(clampToMaxDateTime(newValue, resolvedMaxDateTime), context);
    }
  };

  const handleAccept = (newValue) => {
    if (isValidMomentValue(newValue)) {
      const clamped = clampToMaxDateTime(newValue, resolvedMaxDateTime);
      onChange?.(clamped);
      onAccept?.(clamped);
    }
  };

  return (
    <DateTimePicker
      format={format || getHotelDateTimeFormat()}
      maxDateTime={resolvedMaxDateTime}
      {...props}
      onChange={handleChange}
      onAccept={handleAccept}
    />
  );
}

/** Payment drawers: same as HotelDateTimePicker but future date/time cannot be selected. */
export function PaymentDateTimePicker(props) {
  return <HotelDateTimePicker disableFuture {...props} />;
}

export default HotelDatePicker;
